import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Chip,
  FormLabel,
  Autocomplete,
  Alert,
  IconButton,
  Link,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Grid,
  Stack,
  InputAdornment,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { FormikProps, useFormik } from 'formik';

import * as Yup from 'yup';
import {
  AERevenueDecreaseReasons,
  GrowthSegmentation,
  GrowthSegmentationLabels,
  GrowthSegmentationLabelsToValues,
  Mode,
  PutQuarterlyRevenueStatusDto,
  Quarter,
  QuarterlyRevenueAccount,
  QuarterlyRevenueAccountSummary,
  QuarterlyRevenueSummary,
} from '../../types/quarterlyRevenue';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getLoading,
  getSelectedAccount,
} from '../../redux/quarterlyRevenue/quarterlyRevenue.selectors';
import {
  editQuarterlyRevenueSummary,
  updateQuarterlyRevenueSummary,
} from '../../redux/quarterlyRevenue';
import {
  AgileEngineRevenue,
  ClientGrowth,
  agileEngineRevenueLabels,
  clientGrowthLabels,
} from '../../components/revenueStatusChip/RevenueStatusChip';

import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { COLORS } from '../../utils/colors';
import {
  HeadcountChange,
  QuarterlyRevenueLocation,
  QuarterlyRevenueSeniorities,
  UserWithPermissions,
  techFlowsList,
} from '../../types';
import { fetchAllUsersList, userDataSelectors } from '../../redux/userData';
import {
  fetchHeadcountChanges,
  getHeadcountChanges,
} from '../../redux/headcountChanges';
import { cn } from '../../utils';
import { getSelectedAccount as getSelectedAccountDetails } from '../../redux/account';
import { useParams } from 'react-router-dom';
import { MemberInfoCell } from '../../components/tableCellRenders';
import { DateTime } from 'luxon';
import { NumericFormat } from 'react-number-format';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { emptyValue } from '../../constants';
import { numberFormat, calculateGrowthPercent } from '../../utils';

import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';

interface ForecastModalProps {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  selectedAccount?: string;
  selectedQuarter?: string | undefined;
  selectedQuarterlyRevenue?: QuarterlyRevenueSummary;
  data?: QuarterlyRevenueAccountSummary;
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
}
interface ForecastEditModalProps {
  formik: FormikProps<QuarterlyRevenueFormik>;
  usersList: UserWithPermissions[];
  headcountChanges: HeadcountChange[];
  currentAccount?:
    | QuarterlyRevenueAccount
    | {
        pdmId: string | undefined;
        headCount: number;
        accountName: string;
      };
  loading: boolean;
  resetForm: () => void;
  data?: QuarterlyRevenueAccountSummary;
}

interface ForecastViewModalProps {
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  data?: QuarterlyRevenueAccountSummary;
  currentAccount?:
    | QuarterlyRevenueAccount
    | {
        pdmId: string | undefined;
        headCount: number;
        accountName: string;
      };
  handleCloseModal: () => void;
  affectedPeople?: UserWithPermissions[];
}

export interface QuarterlyRevenueFormik {
  id: string;
  accountId: string;
  quarter: Quarter;
  expectedRevenue: number | null;

  agileRevenue: string;
  aeRevenueReason: AERevenueDecreaseReasons[];
  clientGrowth: string;
  growthSegmentation: GrowthSegmentation | null;
  growthSegmentationValue: number | null;

  overview: string;
  forecast: string;
  growthActions: string;

  hcDelta: number;

  locations?: QuarterlyRevenueLocation[] | null;
  seniorities?: QuarterlyRevenueSeniorities[] | null;
  techFlows?: string[] | null;
  // List of emails as common ids to sent to FSS
  affectedPeople?: UserWithPermissions[] | null;
}

const validationSchema = Yup.object({
  expectedRevenue: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? null : value,
    )
    .min(0, 'Must be zero or greater'),
  agileRevenue: Yup.string().required('AgileEngine revenue is required'),
  aeRevenueReason: Yup.array().when('agileRevenue', {
    is: (value: string) =>
      ![AgileEngineRevenue.FLAT, AgileEngineRevenue.GROWING].includes(
        value as AgileEngineRevenue,
      ),
    then: (schema) => schema.min(1, 'At least one decrease reason is required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  clientGrowth: Yup.string().required('Client growth is required'),
  growthSegmentation: Yup.string().when('clientGrowth', {
    is: (value: string) => value === ClientGrowth.GROWING,
    then: (schema) => schema.required('Growth segmentation is required'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  growthSegmentationValue: Yup.number().when('growthSegmentation', {
    is: (value: string) => value === GrowthSegmentation.EXPAND,
    then: (schema) =>
      schema
        .typeError('Must be a number')
        .required('Required')
        .max(100, '<100%')
        .min(1, '>0%'),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  overview: Yup.string()
    .required('Overview is required')
    .max(5000, 'Max 5000 characters'),
  forecast: Yup.string()
    .required('Forecast is required')
    .max(1500, 'Max 1500 characters'),
  growthActions: Yup.string()
    .required('Growth actions are required')
    .max(1500, 'Max 1500 characters'),
  hcDelta: Yup.number().typeError('Must be a number').required('Required'),
});

const agileRevenueMap: Record<string, AgileEngineRevenue> = {
  Flat: AgileEngineRevenue.FLAT,
  Growing: AgileEngineRevenue.GROWING,
  Decreasing: AgileEngineRevenue.DECREASING,
};

const clientGrowthMap: Record<string, ClientGrowth> = {
  Flat: ClientGrowth.FLAT,
  Growing: ClientGrowth.GROWING,
  Decreasing: ClientGrowth.DECREASING,
  'No info': ClientGrowth.NO_INFO,
};

const ForecastModal: React.FC<ForecastModalProps> = ({
  isModalOpen,
  handleCloseModal,
  selectedAccount,
  selectedQuarter,
  data,
  mode,
  setMode,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getLoading);
  const accounts = useAppSelector(getSelectedAccount);
  const usersList = useAppSelector(userDataSelectors.getAllUsersList);
  const headcountChanges = useAppSelector(getHeadcountChanges);
  const singleAccount = useAppSelector(getSelectedAccountDetails);
  const { accountId } = useParams();

  const quarterNum = selectedQuarter
    ? Number(selectedQuarter) % 100
    : DateTime.now().get('quarter');

  const year = selectedQuarter
    ? Math.floor(Number(selectedQuarter) / 100)
    : DateTime.now().get('year');

  const currentAccount =
    accountId && singleAccount
      ? {
          pdmId: singleAccount.portfolioDeliveryManager?.id,
          headCount:
            data?.headCount ??
            (singleAccount.projects || []).reduce((sum, project) => {
              return sum + (project.members?.length || 0);
            }, 0),
          accountName: singleAccount.name,
        }
      : accounts?.find((acc) => acc.accountId === selectedAccount);

  const affectedPeople = usersList.filter((user) =>
    data?.affectedPeople?.includes(user.email),
  );

  const initialValues: QuarterlyRevenueFormik = data
    ? {
        id: data.id,
        accountId: data.accountId,
        quarter: `Q${parseInt(
          selectedQuarter?.split(' ')[0] ?? '1',
        )}` as Quarter,
        expectedRevenue: data.expectedRevenue ?? null,
        agileRevenue: data.agileEngineRevenue,
        clientGrowth: data.clientGrowth,
        aeRevenueReason: data.aeRevenueDecreaseReasons,
        overview: data.overview,
        forecast: data.forecast,
        growthActions: data.growthActions,
        hcDelta: data.hcDelta,
        growthSegmentation: data.growthSegmentation,
        growthSegmentationValue: data.growthSegmentationValue,
        affectedPeople: affectedPeople ?? [],
        locations: data.locations ?? [],
        seniorities: data.seniorities ?? [],
        techFlows: data.techFlows ?? [],
      }
    : {
        id: '',
        accountId: '',
        quarter: Quarter.Q4,
        expectedRevenue: null,
        agileRevenue: '',
        clientGrowth: '',
        aeRevenueReason: [],
        overview: '',
        forecast: '',
        growthActions: '',
        hcDelta: 0,
        growthSegmentation: null,
        growthSegmentationValue: null,
        affectedPeople: [],
        locations: [],
        seniorities: [],
        techFlows: [],
      };

  const formik = useFormik<QuarterlyRevenueFormik>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!selectedAccount) {
        return;
      }

      const payload: PutQuarterlyRevenueStatusDto = {
        accountId: selectedAccount,
        quarter: parseInt(year + '0' + quarterNum),
        expectedRevenue: values.expectedRevenue ?? undefined,
        agileEngineRevenue: values.agileRevenue as AgileEngineRevenue,
        clientGrowth: values.clientGrowth as ClientGrowth,
        aeRevenueDecreaseReasons: values.aeRevenueReason ?? undefined,
        overview: values.overview,
        forecast: values.forecast,
        growthActions: values.growthActions,
        hcDelta: Number(values.hcDelta),
        affectedPeople:
          values.affectedPeople?.map((option) => option.email) ?? undefined,
        locations: values.locations ?? undefined,
        seniorities: values.seniorities ?? undefined,
        techFlows: values.techFlows ?? undefined,
        growthSegmentation: values.growthSegmentation ?? undefined,
        growthSegmentationValue: values.growthSegmentationValue ?? undefined,
        source: accountId ? 'accountList' : 'accounts',
      };

      if (payload.agileEngineRevenue !== AgileEngineRevenue.DECREASING) {
        payload.aeRevenueDecreaseReasons = undefined;
      }

      if (payload.clientGrowth !== ClientGrowth.GROWING) {
        payload.growthSegmentation = undefined;
        payload.growthSegmentationValue = undefined;
      }

      if (payload.growthSegmentation !== GrowthSegmentation.EXPAND) {
        payload.growthSegmentationValue = undefined;
      }

      if ((payload?.hcDelta ?? 0) >= 0) {
        payload.affectedPeople = undefined;
      }

      if ((payload?.hcDelta ?? 0) <= 0) {
        payload.locations = undefined;
        payload.seniorities = undefined;
        payload.techFlows = undefined;
      }

      if (data?.id)
        await dispatch(editQuarterlyRevenueSummary(payload as any)).unwrap();
      else await dispatch(updateQuarterlyRevenueSummary(payload)).unwrap();

      handleCloseModal();
      formik.resetForm();
    },
  });

  const resetForm = useCallback(() => {
    formik.resetForm();
    handleCloseModal();
  }, [formik, handleCloseModal]);

  useEffect(() => {
    if (!selectedAccount) return;

    dispatch(
      fetchHeadcountChanges({
        accountId: selectedAccount,
        activeId: selectedAccount,
      }),
    );
  }, [selectedAccount, dispatch]);

  useEffect(() => {
    dispatch(fetchAllUsersList());
  }, [dispatch]);

  return (
    <Dialog
      open={isModalOpen}
      onClose={resetForm}
      fullWidth
      maxWidth="md"
      scroll="body"
    >
      <DialogTitle sx={{ position: 'relative', pr: 6 }}>
        {`Quarterly revenue status, Q${quarterNum} of ${year}`}
        <CloseOutlinedIcon
          onClick={resetForm}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
            cursor: 'pointer',
            '&:hover': {
              color: 'text.primary',
            },
          }}
          fontSize="small"
        />
      </DialogTitle>
      {mode === Mode.EDIT ? (
        <ForecastModalEditContent
          headcountChanges={headcountChanges}
          loading={loading}
          usersList={usersList}
          currentAccount={currentAccount}
          resetForm={resetForm}
          formik={formik}
          data={data}
        />
      ) : (
        <ForecastModalViewContent
          data={data}
          handleCloseModal={handleCloseModal}
          setMode={setMode}
          currentAccount={currentAccount}
          affectedPeople={affectedPeople}
        />
      )}
    </Dialog>
  );
};

export default ForecastModal;

const ForecastModalEditContent: React.FC<ForecastEditModalProps> = ({
  usersList,
  headcountChanges,
  currentAccount,
  loading,
  formik,
  resetForm,
  data,
}) => {
  const [hcChangesVisible, setHcChangesVisible] = useState(false);

  const handleSwitchHeadcountChangesTable = useCallback(() => {
    setHcChangesVisible((prev) => !prev);
  }, []);

  const inputRef = useRef<HTMLInputElement>(null);

  const adjustValue = useCallback(
    (value: number) => {
      formik.setFieldValue('hcDelta', Number(formik.values.hcDelta) + value);
      inputRef.current?.focus();
    },
    [formik],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const keyDelta: Record<string, number> = {
        ArrowUp: 1,
        ArrowDown: -1,
      };
      const delta = keyDelta[event.key];
      if (delta) {
        event.preventDefault();
        adjustValue(delta);
      }
    },
    [adjustValue],
  );

  const iconButtonSx = {
    p: 0,
    height: '50%',
    width: '100%',
    borderRadius: 0,
    '&:hover': { backgroundColor: '#e0e0e0' },
    cursor: 'default',
    overflow: 'hidden',
  };
  return (
    <>
      <DialogContent sx={{ pt: 3, px: 0, pb: 0 }}>
        <Alert
          severity="info"
          variant="filled"
          elevation={0}
          icon={
            <IconButton
              sx={{
                color: 'white',
                background: COLORS.BRAND.PRIMARY,
                borderRadius: '12px',
                cursor: 'default',
                ':hover': { color: 'white', background: COLORS.BRAND.PRIMARY },
              }}
            >
              <WorkOutlineIcon />
            </IconButton>
          }
          sx={{
            marginBottom: 2,
            width: '100%',
            borderRadius: 0 + '!important',
            backgroundColor: 'background.default',
            paddingBlock: 0,
            paddingInline: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flexGrow={1}>
              <Typography variant="h5" color="text.secondary">
                Account
              </Typography>
              <Typography variant="h5" color="text.primary">
                {currentAccount?.accountName || 'N/A'}
              </Typography>
            </Box>
            <Button
              href="https://docs.google.com/document/d/1hetB4EolOYiC5TCnHCGp3BkOjuUAeOGErf6yIiRFSbU/edit?tab=t.0#heading=h.ojr2ld2me0p9"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <MenuBookOutlinedIcon sx={{ marginRight: 1 }} />
              Forecast guidelines ↗
            </Button>
          </Box>
        </Alert>

        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          paddingInline={'24px'}
        >
          <Box display="flex" gap={2} alignItems="flex-start" flexWrap="nowrap">
            <NumericFormat
              label="Forecasted revenue"
              customInput={TextField}
              sx={{ width: 200, flexShrink: 0 }}
              value={formik.values.expectedRevenue ?? ''}
              thousandSeparator
              decimalScale={2}
              allowNegative={false}
              InputLabelProps={{ shrink: true }}
              onValueChange={(values) => {
                formik.setFieldValue(
                  'expectedRevenue',
                  values.floatValue ?? null,
                );
              }}
              error={
                formik.touched.expectedRevenue &&
                Boolean(formik.errors.expectedRevenue)
              }
              helperText={
                formik.touched.expectedRevenue && formik.errors.expectedRevenue
                  ? formik.errors.expectedRevenue
                  : ''
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
            {(() => {
              const prevRevenueQuarter =
                data?.previousQuarterRevenueQuarter ??
                (currentAccount &&
                'previousQuarterRevenueQuarter' in currentAccount
                  ? (
                      currentAccount as {
                        previousQuarterRevenueQuarter?: number | null;
                      }
                    ).previousQuarterRevenueQuarter
                  : null);
              const prevQ = prevRevenueQuarter ? prevRevenueQuarter % 100 : '';
              const prevRevenue =
                data?.previousQuarterRevenue ??
                (currentAccount && 'previousQuarterRevenue' in currentAccount
                  ? (
                      currentAccount as {
                        previousQuarterRevenue?: number | null;
                      }
                    ).previousQuarterRevenue
                  : null);
              const liveGrowthPercent = calculateGrowthPercent(
                formik.values.expectedRevenue,
                prevRevenue,
              );
              const disabledFieldSx = {
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.default',
                  borderRadius: 1,
                  '& fieldset': { border: 'none' },
                },
                '& .MuiOutlinedInput-root.Mui-disabled': {
                  backgroundColor: 'background.default',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                },
                '& .MuiInputAdornment-root .MuiTypography-root': {
                  color: 'text.secondary',
                },
              };
              return (
                <>
                  <TextField
                    label={`Actuals for Q${prevQ}`}
                    variant="outlined"
                    disabled
                    value={
                      prevRevenue != null
                        ? numberFormat(prevRevenue, undefined, 0)
                        : 'N/A'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={{ ...disabledFieldSx, width: 160, flexShrink: 0 }}
                  />
                  {liveGrowthPercent != null && (
                    <>
                      <TextField
                        label="Growth"
                        variant="outlined"
                        disabled
                        value={liveGrowthPercent}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                        sx={{ ...disabledFieldSx, width: 110, flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          alignSelf: 'center',
                          lineHeight: 1.3,
                          minWidth: 0,
                        }}
                      >
                        Calculated from expected revenue vs actuals of Q{prevQ}
                      </Typography>
                    </>
                  )}
                </>
              );
            })()}
          </Box>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Box>
              <FormLabel
                sx={{
                  mb: 1.5,
                  display: 'block',
                  fontSize: '0.875rem',
                  color: 'text.primary',
                }}
              >
                AgileEngine revenue forecast
              </FormLabel>
              <RadioGroup
                row
                value={formik.values.agileRevenue}
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '8px',
                  p: '8px 12px',
                  gap: 1,
                }}
                onChange={(e) => {
                  formik.setFieldValue('agileRevenue', e.target.value);
                  if (
                    [
                      AgileEngineRevenue.FLAT,
                      AgileEngineRevenue.GROWING,
                    ].includes(e.target.value as AgileEngineRevenue)
                  )
                    formik.setFieldValue('aeRevenueReason', [], true);
                  formik.validateForm();
                }}
              >
                {Object.entries(agileRevenueMap).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    value={value}
                    control={<Radio size="small" />}
                    label={key}
                    sx={{ margin: 0 }}
                  />
                ))}
              </RadioGroup>
              {formik.touched.agileRevenue && formik.errors.agileRevenue && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 0.5, ml: 1.5, display: 'block' }}
                >
                  {formik.errors.agileRevenue}
                </Typography>
              )}
            </Box>

            {[AgileEngineRevenue.DECREASING].includes(
              formik.values.agileRevenue as AgileEngineRevenue,
            ) && (
              <Box>
                <Autocomplete
                  multiple
                  options={Object.values(AERevenueDecreaseReasons)}
                  value={formik.values.aeRevenueReason || []}
                  onChange={(_, value) =>
                    formik.setFieldValue('aeRevenueReason', value)
                  }
                  renderTags={(
                    value: AERevenueDecreaseReasons[],
                    getTagProps,
                  ) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{
                            borderRadius: '20px',
                            m: 0.25,
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AE revenue decrease reasons"
                      placeholder="Please select"
                      error={
                        formik.touched.aeRevenueReason &&
                        Boolean(formik.errors.aeRevenueReason)
                      }
                      helperText={
                        formik.touched.aeRevenueReason &&
                        formik.errors.aeRevenueReason
                      }
                    />
                  )}
                />
              </Box>
            )}
            <Box>
              <FormLabel
                sx={{
                  mb: 1.5,
                  display: 'block',
                  fontSize: '0.875rem',
                  color: 'text.primary',
                }}
              >
                Client growth indicator
              </FormLabel>
              <RadioGroup
                row
                value={formik.values.clientGrowth}
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '8px',
                  p: '8px 12px',
                  gap: 1,
                }}
                onChange={(e) =>
                  formik.setFieldValue('clientGrowth', e.target.value)
                }
              >
                {Object.entries(clientGrowthMap).map(([key, value]) => (
                  <FormControlLabel
                    key={key}
                    value={value}
                    control={<Radio size="small" />}
                    label={key === 'No info' ? 'No info available' : key}
                    sx={{ margin: 0 }}
                  />
                ))}
              </RadioGroup>
              {formik.touched.clientGrowth && formik.errors.clientGrowth && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 0.5, ml: 1.5, display: 'block' }}
                >
                  {formik.errors.clientGrowth}
                </Typography>
              )}
            </Box>
          </Box>

          {formik.values.clientGrowth === ClientGrowth.GROWING && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Autocomplete
                  options={Object.values(GrowthSegmentationLabels)}
                  sx={{ width: '100%' }}
                  value={
                    formik.values.growthSegmentation
                      ? GrowthSegmentationLabels[
                          formik.values
                            .growthSegmentation as keyof typeof GrowthSegmentationLabels
                        ]
                      : null
                  }
                  onChange={(_, value) => {
                    {
                      formik.setFieldValue(
                        'growthSegmentation',
                        GrowthSegmentationLabelsToValues[
                          value as keyof typeof GrowthSegmentationLabelsToValues
                        ],
                      );
                      if (
                        GrowthSegmentationLabelsToValues[
                          value as keyof typeof GrowthSegmentationLabelsToValues
                        ] === GrowthSegmentation.EXPAND &&
                        !formik.values.growthSegmentationValue
                      ) {
                        formik.setFieldValue('growthSegmentationValue', 1);
                      }
                    }
                  }}
                  renderTags={(value: any[]) =>
                    value.map((option) => {
                      return (
                        <>
                          <Chip key={option} label={value} size="small" />
                        </>
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Growth segmentation"
                      placeholder="Please select"
                      required
                      error={
                        formik.touched.growthSegmentation &&
                        Boolean(formik.errors.growthSegmentation)
                      }
                      helperText={
                        formik.touched.affectedPeople &&
                        formik.errors.affectedPeople
                      }
                    />
                  )}
                />
                {formik.values.growthSegmentation ===
                  GrowthSegmentation.EXPAND && (
                  <>
                    <TextField
                      label="Percentage"
                      type="number"
                      value={formik.values.growthSegmentationValue}
                      onChange={(e) =>
                        formik.setFieldValue(
                          'growthSegmentationValue',
                          Number(e.target.value),
                        )
                      }
                      error={
                        formik.touched.growthSegmentationValue &&
                        Boolean(formik.errors.growthSegmentationValue)
                      }
                      helperText={
                        formik.touched.growthSegmentationValue &&
                        formik.errors.growthSegmentationValue
                      }
                      sx={{ width: '120px', flexShrink: 0 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5 }}
                    >
                      Expected growth percentage
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          <Box>
            <TextField
              label="Overview"
              multiline
              minRows={4}
              fullWidth
              value={formik.values.overview}
              onChange={(e) => formik.setFieldValue('overview', e.target.value)}
              error={formik.touched.overview && Boolean(formik.errors.overview)}
              helperText={
                formik.touched.overview && formik.errors.overview
                  ? formik.errors.overview
                  : ''
              }
              placeholder="Briefly describe achievements and issues at the business level, and how they affected our revenue growth and relationship with the client of the quarter that have just ended."
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <TextField
              label="Forecast"
              multiline
              minRows={4}
              fullWidth
              value={formik.values.forecast}
              onChange={(e) => formik.setFieldValue('forecast', e.target.value)}
              error={formik.touched.forecast && Boolean(formik.errors.forecast)}
              helperText={
                formik.touched.forecast && formik.errors.forecast
                  ? formik.errors.forecast
                  : ''
              }
              placeholder="Briefly describe the outlook for the next quarter and expectations for the rest of the year based on the current situation. Will the revenue grow, stay flat, or decrease based on the information we have."
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <TextField
              label="Growth actions"
              multiline
              minRows={4}
              fullWidth
              value={formik.values.growthActions}
              onChange={(e) =>
                formik.setFieldValue('growthActions', e.target.value)
              }
              error={
                formik.touched.growthActions &&
                Boolean(formik.errors.growthActions)
              }
              helperText={
                formik.touched.growthActions && formik.errors.growthActions
                  ? formik.errors.growthActions
                  : ''
              }
              placeholder="Briefly describe what we do to grow revenue. For flat and decreasing accounts what will we do to improve situation."
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <NumericFormat
                label="HC delta"
                customInput={TextField}
                value={Number(formik.values.hcDelta).toFixed(1)}
                decimalSeparator="."
                decimalScale={2}
                onValueChange={(values) => {
                  formik.setFieldValue('hcDelta', values.floatValue ?? '');
                }}
                onKeyDown={handleKeyDown}
                error={formik.touched.hcDelta && Boolean(formik.errors.hcDelta)}
                helperText={formik.touched.hcDelta && formik.errors.hcDelta}
                sx={{
                  width: '120px',
                  flexShrink: 0,
                  '& .MuiInputAdornment-root': {
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                  },
                  '&:hover .MuiInputAdornment-root': {
                    opacity: 1,
                  },
                  '& .Mui-focused .MuiInputAdornment-root': {
                    opacity: 1,
                  },
                }}
                inputRef={inputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ ml: 0 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'end',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '2px',
                          width: '15px',
                          height: '15px',
                        }}
                      >
                        <IconButton
                          disableRipple
                          onClick={() => adjustValue(1)}
                          sx={iconButtonSx}
                        >
                          <ArrowDropUp
                            sx={{ fontSize: '17px', color: '#000' }}
                          />
                        </IconButton>

                        <IconButton
                          disableRipple
                          onClick={() => adjustValue(-1)}
                          sx={iconButtonSx}
                        >
                          <ArrowDropDown
                            sx={{ fontSize: '17px', color: '#000' }}
                          />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2.5, lineHeight: 1.5 }}
              >
                Expected allocation weighted headcount joining or leaving the
                account in the upcoming quarter.{' '}
                <Link
                  target="_blank"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleSwitchHeadcountChangesTable()}
                >
                  Planned changes ↑
                </Link>
              </Typography>
            </Box>
          </Box>
          {hcChangesVisible && (
            <>
              {headcountChanges.length > 0 ? (
                <Box
                  sx={{
                    mt: 2,
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    p: 1,
                    width: '30%',
                  }}
                >
                  <Typography variant="body2" gutterBottom>
                    Planned changes
                  </Typography>
                  <Table>
                    <TableHead
                      sx={{
                        '& th, & td': {
                          background: 'transparent',
                          bgcolor: 'transparent',
                        },
                      }}
                    >
                      <TableRow className={cn('no-height')}>
                        <TableCell sx={{ padding: '8px 16px' }}>
                          <Typography variant="body2">Date</Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            padding: '8px 16px',
                            backgroundColor: 'background.default',
                          }}
                        >
                          <Typography variant="body2">HC delta</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {headcountChanges.map((change, idx) => (
                        <TableRow key={idx} className={cn('no-height')}>
                          <TableCell sx={{ padding: '8px 16px' }}>
                            {change.date
                              ? new Date(change.date).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ padding: '8px 16px' }}>
                            {change.countChange ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      px: 1,
                      backgroundColor: 'background.default',
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    There are no planned changes with the headcount for this
                    account.
                  </Typography>
                </>
              )}
            </>
          )}
          {/** Affected people */}
          {formik.values.hcDelta < 0 && (
            <>
              <Box>
                <Autocomplete
                  multiple
                  options={usersList}
                  getOptionLabel={(option) => option.fullName}
                  value={formik.values.affectedPeople || []}
                  onChange={(_, value) =>
                    formik.setFieldValue('affectedPeople', value)
                  }
                  // isOptionEqualToValue={(option, value) =>
                  //   option.email === value.email
                  // }
                  renderTags={(value: UserWithPermissions[], getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option.fullName}
                          {...tagProps}
                          size="small"
                          sx={{
                            borderRadius: '20px',
                            m: 0.25,
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Affected people"
                      placeholder="Optional"
                      error={
                        formik.touched.affectedPeople &&
                        Boolean(formik.errors.affectedPeople)
                      }
                      helperText={
                        formik.touched.affectedPeople &&
                        formik.errors.affectedPeople
                      }
                    />
                  )}
                />
              </Box>
            </>
          )}
          {/** Location, Seniority, Tech flow */}
          {formik.values.hcDelta > 0 && (
            <>
              <Box>
                <Autocomplete
                  multiple
                  options={Object.values(QuarterlyRevenueLocation)}
                  value={formik.values.locations || []}
                  onChange={(_, value) =>
                    formik.setFieldValue('locations', value)
                  }
                  renderTags={(
                    value: QuarterlyRevenueLocation[],
                    getTagProps,
                  ) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{
                            borderRadius: '20px',
                            m: 0.25,
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Location"
                      placeholder="Optional"
                      error={
                        formik.touched.locations &&
                        Boolean(formik.errors.locations)
                      }
                      helperText={
                        formik.touched.locations && formik.errors.locations
                      }
                    />
                  )}
                />
              </Box>
              <Box>
                <Autocomplete
                  multiple
                  options={Object.values(QuarterlyRevenueSeniorities)}
                  value={formik.values.seniorities || []}
                  onChange={(_, value) =>
                    formik.setFieldValue('seniorities', value)
                  }
                  renderTags={(
                    value: QuarterlyRevenueSeniorities[],
                    getTagProps,
                  ) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{
                            borderRadius: '20px',
                            m: 0.25,
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Seniority"
                      placeholder="Optional"
                      error={
                        formik.touched.seniorities &&
                        Boolean(formik.errors.seniorities)
                      }
                      helperText={
                        formik.touched.seniorities && formik.errors.seniorities
                      }
                    />
                  )}
                />
              </Box>
              <Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={techFlowsList}
                  value={formik.values.techFlows || []}
                  onChange={(_, value) =>
                    formik.setFieldValue('techFlows', value)
                  }
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={option}
                          {...tagProps}
                          size="small"
                          sx={{
                            borderRadius: '20px',
                            m: 0.25,
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tech flow"
                      placeholder="Optional"
                      error={
                        formik.touched.techFlows &&
                        Boolean(formik.errors.techFlows)
                      }
                      helperText={
                        formik.touched.techFlows && formik.errors.techFlows
                      }
                    />
                  )}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={resetForm}>Cancel</Button>
        <Button
          variant="contained"
          onClick={formik.submitForm}
          disabled={!formik.dirty || loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </>
  );
};

const ForecastModalViewContent: React.FC<ForecastViewModalProps> = ({
  data,
  handleCloseModal,
  setMode,
  currentAccount,
  affectedPeople,
}) => {
  const renderChip = useCallback(
    (item: string | number, index: number, isHCDelta?: boolean) => (
      <Chip
        key={index}
        label={`${isHCDelta && +item > 0 ? '+' : ''}${item}`}
        size="small"
        sx={{
          fontSize: '0.75rem',
          color: isHCDelta ? 'white' : 'text.primary',
          px: isHCDelta ? 0.5 : 0,
          backgroundColor: isHCDelta
            ? +item < 0
              ? COLORS.STATUS.WARNING
              : COLORS.STATUS.SUCCESS
            : '',
        }}
      />
    ),
    [],
  );
  return (
    <>
      <DialogContent sx={{ pt: 3, px: 0, pb: 0 }}>
        <Alert
          severity="info"
          variant="filled"
          elevation={0}
          icon={
            <IconButton
              sx={{
                color: 'white',
                background: COLORS.BRAND.PRIMARY,
                borderRadius: '12px',
                cursor: 'default',
                ':hover': { color: 'white', background: COLORS.BRAND.PRIMARY },
              }}
            >
              <WorkOutlineIcon />
            </IconButton>
          }
          sx={{
            marginBottom: 2,
            width: '100%',
            borderRadius: 0 + '!important',
            backgroundColor: 'background.default',
            paddingBlock: 0,
            paddingInline: 3,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box flexGrow={1}>
              <Typography variant="h5" color="text.secondary">
                Account
              </Typography>
              <Typography variant="h5" color="text.primary">
                {currentAccount?.accountName || 'N/A'}
              </Typography>
            </Box>
            <Button
              href="https://docs.google.com/document/d/1hetB4EolOYiC5TCnHCGp3BkOjuUAeOGErf6yIiRFSbU/edit?tab=t.0#heading=h.ojr2ld2me0p9"
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <MenuBookOutlinedIcon sx={{ marginRight: 1 }} />
              Forecast guidelines ↗
            </Button>
          </Box>
        </Alert>
        <Box paddingInline={3}>
          <Box marginBottom={3} display="flex" gap={3} flexWrap="wrap">
            <Box>
              <Typography textAlign="start" variant="body2" component="h5">
                Forecasted revenue ($)
              </Typography>
              <Typography
                textAlign="start"
                sx={{ marginBlock: 1 }}
                variant="body1"
                component="h5"
              >
                {data?.expectedRevenue !== null &&
                data?.expectedRevenue !== undefined
                  ? `$ ${numberFormat(data.expectedRevenue, undefined, 0)}`
                  : emptyValue}
              </Typography>
            </Box>
            <Box>
              <Typography textAlign="start" variant="body2" component="h5">
                Actuals for Q
                {data?.quarter
                  ? data.quarter % 100 === 1
                    ? 4
                    : (data.quarter % 100) - 1
                  : ''}
              </Typography>
              <Typography
                textAlign="start"
                sx={{ marginBlock: 1 }}
                variant="body1"
                component="h5"
              >
                {data?.previousQuarterRevenue != null
                  ? `$ ${numberFormat(
                      data.previousQuarterRevenue,
                      undefined,
                      0,
                    )}`
                  : 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography textAlign="start" variant="body2" component="h5">
                Growth
              </Typography>
              {(() => {
                const growthPercent = calculateGrowthPercent(
                  data?.expectedRevenue,
                  data?.previousQuarterRevenue,
                );
                return (
                  <Typography
                    textAlign="start"
                    sx={{
                      marginBlock: 1,
                      color:
                        growthPercent != null
                          ? growthPercent >= 0
                            ? 'success.dark'
                            : 'error.dark'
                          : 'text.primary',
                    }}
                    variant="body1"
                    component="h5"
                  >
                    {growthPercent != null
                      ? `${growthPercent > 0 ? '+' : ''}${growthPercent}%`
                      : emptyValue}
                  </Typography>
                );
              })()}
            </Box>
          </Box>
          <Grid container>
            <Grid item xs={3}>
              <Typography textAlign="start" variant="body2" component="h5">
                AgileEngine <br /> revenue forecast
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography textAlign="start" variant="body2" component="h5">
                Client growth <br /> indicator
              </Typography>
            </Grid>
            <Grid item xs={3}>
              {data?.growthSegmentation && (
                <Typography textAlign="start" variant="body2" component="h5">
                  Growth <br /> segmentation
                </Typography>
              )}
            </Grid>
            <Grid item xs={3}>
              {data?.growthSegmentationValue && (
                <Typography textAlign="start" variant="body2" component="h5">
                  Expected growth <br /> percentage
                </Typography>
              )}
            </Grid>
            <Grid item xs={3}>
              <Typography
                textAlign="start"
                sx={{
                  marginBlock: 1,
                }}
                variant="body1"
                component="h5"
              >
                {
                  agileEngineRevenueLabels[
                    data?.agileEngineRevenue as keyof typeof AgileEngineRevenue
                  ]
                }
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                textAlign="start"
                sx={{
                  marginBlock: 1,
                }}
                variant="body1"
                component="h5"
              >
                {
                  clientGrowthLabels[
                    data?.clientGrowth as keyof typeof ClientGrowth
                  ]
                }
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                textAlign="start"
                sx={{
                  marginBlock: 1,
                }}
                variant="body1"
                component="h5"
              >
                {
                  GrowthSegmentationLabels[
                    data?.growthSegmentation as keyof typeof GrowthSegmentation
                  ]
                }
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography
                textAlign="start"
                sx={{
                  marginBlock: 1,
                }}
                variant="body1"
                component="h5"
              >
                {data?.growthSegmentationValue}
              </Typography>
            </Grid>
          </Grid>
          <Grid container marginTop={3}>
            <Grid item xs={12}>
              <Typography textAlign="start" variant="body2" component="h5">
                AE revenue decrease reasons
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                marginTop={1}
                marginBottom={2}
              >
                {data?.aeRevenueDecreaseReasons ? (
                  data.aeRevenueDecreaseReasons.map((reason, index) =>
                    renderChip(reason, index),
                  )
                ) : (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                  >
                    &mdash;
                  </Box>
                )}
              </Box>
              <Typography textAlign="start" variant="body2" component="h5">
                Overview
              </Typography>
              <Typography
                textAlign="start"
                sx={{
                  cursor: 'default',
                }}
                variant="body1"
                component="h5"
                marginBottom={2}
              >
                {data?.overview}
              </Typography>

              <Typography textAlign="start" variant="body2" component="h5">
                Forecast
              </Typography>
              <Typography
                textAlign="start"
                sx={{
                  cursor: 'default',
                }}
                variant="body1"
                component="h5"
                marginBottom={2}
              >
                {data?.forecast}
              </Typography>

              <Typography textAlign="start" variant="body2" component="h5">
                Growth actions
              </Typography>
              <Typography
                textAlign="start"
                sx={{
                  cursor: 'default',
                }}
                variant="body1"
                component="h5"
                marginBottom={2}
              >
                {data?.growthActions}
              </Typography>

              <Typography textAlign="start" variant="body2" component="h5">
                HC delta
              </Typography>

              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                marginTop={1}
                marginBottom={2}
              >
                {data?.hcDelta ? (
                  renderChip(Number(data?.hcDelta).toFixed(1), 1, true)
                ) : (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                  >
                    &mdash;
                  </Box>
                )}
              </Box>

              <Typography textAlign="start" variant="body2" component="h5">
                Affected people
              </Typography>

              {affectedPeople?.length ? (
                affectedPeople?.map((affectedPeopleData, index) => (
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    sx={{
                      marginBlock: 1,
                      paddingBlock: 1,
                      borderBottom:
                        index === affectedPeople.length - 1
                          ? ''
                          : '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                    key={affectedPeopleData.id}
                  >
                    <MemberInfoCell
                      hideAvatar={!affectedPeopleData.avatarUrl}
                      userInfo={{
                        avatarUrl: affectedPeopleData.avatarUrl,
                        fullName:
                          affectedPeopleData.fullName ??
                          affectedPeopleData.email ??
                          '',
                        stream: affectedPeopleData.designation,
                        country: affectedPeopleData.country,
                      }}
                    />
                  </Stack>
                ))
              ) : (
                <Box
                  component="span"
                  sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                >
                  &mdash;
                </Box>
              )}

              <Typography textAlign="start" variant="body2" component="h5">
                Hiring Locations
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                marginTop={1}
                marginBottom={2}
              >
                {data?.locations ? (
                  data.locations.map((location, index) =>
                    renderChip(location, index),
                  )
                ) : (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                  >
                    Not specified
                  </Box>
                )}
              </Box>

              <Typography textAlign="start" variant="body2" component="h5">
                Seniority
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                marginTop={1}
                marginBottom={2}
              >
                {data?.seniorities ? (
                  data.seniorities.map((seniority, index) =>
                    renderChip(seniority, index),
                  )
                ) : (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                  >
                    Not specified
                  </Box>
                )}
              </Box>

              <Typography textAlign="start" variant="body2" component="h5">
                Technical flow
              </Typography>
              <Box
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                marginTop={1}
                marginBottom={2}
              >
                {data?.techFlows ? (
                  data.techFlows.map((techFlow, index) =>
                    renderChip(techFlow, index),
                  )
                ) : (
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 0.5, pr: 2.5 }}
                  >
                    Not specified
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button variant="contained" onClick={() => setMode(Mode.EDIT)}>
          Edit
        </Button>
        <Button onClick={handleCloseModal}>Close</Button>
      </DialogActions>
    </>
  );
};
